'use client'

import React from 'react'
import useSwr, { useSWRConfig } from 'swr'
import { BadgeCheck, Check, Copy, ExternalLink, User } from 'lucide-react'
import { cn } from '@/lib/utils'
import { API_URL } from '@/utils/constants'
import event from '@/utils/event'
import Wallet from '@/utils/wallet'
import useAlkanesBalance from '@/utils/hooks/useAlkanesBalance'
import { BTC_ID, CURVE_LP_ID, formatAmount, frbtcMidPriceScaled, parseAmount } from '../lib/amm'
import { usdCompact, truncateMiddle } from '../lib/format'
import { strictFetcher } from '../lib/strict-fetcher'
import { twetchJson, TWETCH_WEB } from '../lib/twetch-api'
import { resolveTwetchImage } from '../lib/twetch-media'
import { useAlkanes } from '../lib/store'
import useHistory from '../lib/useHistory'
import useIsDesktop from '../lib/useIsDesktop'
import useTwetchIdentities from '../lib/useTwetchIdentities'
import useTwetchPfp from '../lib/use-twetch-pfp'
import useLedger, { type LedgerToken } from '../lib/useLedger'
import useEquity, { type EquityPoint } from '../lib/useEquity'
import { ledgerPosition } from '../lib/ledger-display'
import HistoryList from './history-list'
import { FollowRail } from './profile'
import { Avatar } from './social-feed'
import { openProfileEditor } from '../lib/twetch-ui'
import { viewerProfileKey } from '../lib/use-viewer-profile'
import WorkspaceRail from './workspace-rail'
import FollowButton from './follow-button'
import featureFlags from '@/utils/feature-flags'
import { SOCIAL_PROFILES_FLAG } from './user-hover-card'

// Flags are baked into the bundle, so one read serves every section below.
const socialProfiles = featureFlags(SOCIAL_PROFILES_FLAG)

/// The full public profile, as `GET /v1/users/:id` serves it — a superset of
/// the batch identity used in wallet columns.
interface TwetchProfile {
	id: number
	name?: string | null
	icon?: string | null
	banner?: string | null
	description?: string | null
	verifiedIcon?: string | null
	numFollowers?: number
	numFollowing?: number
}

interface Position {
	id: string
	name: string
	icon: string | null
	raw: bigint
	usd: number | null
}


/// Sub-unit balances need the full 8dp — 4dp rounds a real position to a
/// fabricated "0".
function amountDp(raw: bigint): number {
	return raw < 100_000_000n ? 8 : 4
}

/// The canonical public profile of one address: who it is on Twetch (when the
/// wallet is linked), what it holds, and the trades decoded from its own
/// transactions. Desktop wears the SAME page shape as the own-profile screen
/// (identity card, value + swaps grid, holdings, follow rail); the phone gets
/// the stacked column. This is the destination every wallet link in the app
/// points at now — explorers are one more click away, from here.
export default function WalletProfile() {
	const address = useAlkanes((s) => s.profileAddress)
	if (!address) {
		return null
	}
	// Keyed by address so switching profiles resets copy state and filters.
	return <ProfileBody key={address} address={address} />
}

function ProfileBody({ address }: { address: string }) {
	const desktop = useIsDesktop()
	const pools = useAlkanes((s) => s.pools)
	const btcUsd = useAlkanes((s) => s.btcUsd)
	const navigate = useAlkanes((s) => s.navigate)

	// Identity: the same batch cache the wallet columns use finds the Twetch
	// user; the full profile (banner, counts, bio) is one more public read.
	const identities = useTwetchIdentities([address])
	// The owner's own edit arrives through the caches these two reads share
	// with the app-wide profile editor (identity index + profile key), so it
	// shows here at once without re-reading a profile the browser has cached.
	const base = identities[address]
	const { data: profile } = useSwr<TwetchProfile>(
		base ? viewerProfileKey(base.id) : null,
		() => twetchJson<TwetchProfile>(`/v1/users/${base!.id}`),
		{ revalidateOnFocus: false }
	)

	// Holdings — the same public reads the watch mode uses, scoped here.
	const { data: balanceItems, isLoading: balancesLoading, error: balancesError } =
		useAlkanesBalance(address)
	const { data: btcBalance } = useSwr<{ confirmed_balance?: number }>(
		`${API_URL}/wallet/${address}/balance`,
		strictFetcher,
		{ revalidateOnFocus: false }
	)

	// Own wallet gets the Send/Receive doors and merges this browser's own
	// broadcast records into its history; someone else's does not.
	const own = Wallet.getTaprootAddress() === address
	// The same history the list renders (SWR dedups the request) — here for
	// the header's trade count and the unavailable banner.
	const history = useHistory(address, own)

	// History sees a block before the balance read does (its CDN copy lives
	// 15s), and nothing else refetches balances — so a confirmed buy could sit
	// in the list with no holding beside it. Any change at the top of the
	// history refetches balances now, and once more after the CDN copy expires.
	const { mutate: mutateSwr } = useSWRConfig()
	const historyHead = `${history.rows[0]?.txid ?? ''}:${history.pendingCount}`
	React.useEffect(() => {
		if (historyHead === ':0') return
		const refetch = () => {
			void mutateSwr(['/alkanes-balance', address])
			void mutateSwr(`${API_URL}/wallet/${address}/balance`)
		}
		refetch()
		const timer = window.setTimeout(refetch, 20_000)
		return () => window.clearTimeout(timer)
	}, [historyHead, address, mutateSwr])

	// The server-side full-history fold, for per-position PnL. Works for any
	// address; rows without it (endpoint down, unpriced token) simply show
	// no PnL figure rather than a fabricated one.
	const ledger = useLedger(address)

	const usdOf = React.useCallback(
		(id: string, raw: bigint): number | null => {
			if (btcUsd === null) {
				return null
			}
			if (id === BTC_ID) {
				return (Number(raw) / 1e8) * btcUsd
			}
			const mid = frbtcMidPriceScaled(pools, id)
			if (mid === null) {
				return null
			}
			return (Number(raw * mid) / 1e24) * btcUsd
		},
		[pools, btcUsd]
	)

	const positions = React.useMemo(() => {
		const rows: Position[] = []
		const confirmed = btcBalance?.confirmed_balance
		if (typeof confirmed === 'number' && Number.isFinite(confirmed) && confirmed > 0) {
			const raw = BigInt(Math.floor(confirmed))
			rows.push({
				id: BTC_ID,
				name: 'BTC',
				// The site's own BTC mark — the "BT" initials fallback is for
				// tokens without art, and BTC is not one of those.
				icon: '/market/btc-collection.svg',
				raw,
				usd: usdOf(BTC_ID, raw)
			})
		}
		if (Array.isArray(balanceItems)) {
			for (const item of balanceItems) {
				if (!item.rune_id) {
					continue
				}
				const id = String(item.rune_id)
				let raw = parseAmount(String(item.overall_balance)) ?? 0n
				if (raw <= 0n) {
					continue
				}
				// The curve LP is 18-decimal by construction — normalize at the
				// door like every other surface, or it renders ×10^10.
				if (id === CURVE_LP_ID) {
					raw = raw / 10_000_000_000n
				}
				const poolToken = pools.flatMap((p) => [p.token0, p.token1]).find((t) => t.id === id)
				rows.push({
					id,
					name: item.ticker ? String(item.ticker) : poolToken?.name ?? id,
					icon: item.collection?.icon ? String(item.collection.icon) : poolToken?.icon ?? null,
					raw,
					usd: usdOf(id, raw)
				})
			}
		}
		// Priced rows by value, then the unpriced by size — never fabricating
		// a $0 for a token no pool prices.
		return rows.sort((a, b) => {
			if (a.usd !== null && b.usd !== null) return b.usd - a.usd
			if (a.usd !== null) return -1
			if (b.usd !== null) return 1
			return b.raw > a.raw ? 1 : -1
		})
	}, [balanceItems, btcBalance, pools, usdOf])

	const totalUsd = React.useMemo(() => {
		let sum = 0
		let priced = 0
		for (const r of positions) {
			if (r.usd !== null) {
				sum += r.usd
				priced += 1
			}
		}
		return priced > 0 ? sum : null
	}, [positions])
	const cashUsd = positions.find((p) => p.id === BTC_ID)?.usd ?? null

	const [copied, setCopied] = React.useState(false)
	const copy = React.useCallback(() => {
		void navigator.clipboard?.writeText(address).then(() => {
			setCopied(true)
			window.setTimeout(() => setCopied(false), 1500)
		})
	}, [address])

	const shared = {
		address,
		base,
		profile,
		positions,
		totalUsd,
		cashUsd,
		history,
		balancesLoading,
		balancesUnavailable: balancesError !== undefined,
		pools,
		navigate,
		copied,
		copy,
		ledgerTokens: ledger.tokens,
		own,
	}

	if (desktop) {
		return (
			<section aria-label="Wallet profile" data-testid="alkanes-wallet-profile">
				<DesktopProfile {...shared} />
			</section>
		)
	}
	return (
		<section
			aria-label="Wallet profile"
			data-testid="alkanes-wallet-profile"
			className="px-3 pb-6 pt-3"
		>
			<PhoneProfile {...shared} />
		</section>
	)
}

interface ProfileProps {
	address: string
	base: ReturnType<typeof useTwetchIdentities>[string] | undefined
	profile: TwetchProfile | undefined
	positions: Position[]
	totalUsd: number | null
	cashUsd: number | null
	history: ReturnType<typeof useHistory>
	balancesLoading: boolean
	balancesUnavailable: boolean
	pools: ReturnType<typeof useAlkanes.getState>['pools']
	navigate: ReturnType<typeof useAlkanes.getState>['navigate']
	copied: boolean
	copy: () => void
	ledgerTokens: Map<string, LedgerToken> | undefined
	own: boolean
}

// ── shared fragments ───────────────────────────────────────────────────────

function IdentityName({ base, profile, address }: Pick<ProfileProps, 'base' | 'profile' | 'address'>) {
	const name = profile?.name ?? base?.name ?? null
	const verified = profile?.verifiedIcon ?? base?.verifiedIcon ?? null
	return (
		<span className="flex min-w-0 items-center gap-1.5">
			<span className="truncate text-lg font-semibold" data-testid="alkanes-profile-name">
				{name ?? truncateMiddle(address)}
			</span>
			{verified && (
				<BadgeCheck
					className="size-4 shrink-0 text-primary"
					aria-label={`Verified (${verified})`}
				/>
			)}
		</span>
	)
}

/// What the profile picture IS, when it is a Bitcoin inscription: its name,
/// collection and number, linking to the inscription itself.
function PfpRow({ icon }: { icon: string | null | undefined }) {
	const pfp = useTwetchPfp(icon)
	if (!pfp) {
		return null
	}
	return (
		<a
			href={`/inscription/${pfp.id}`}
			data-testid="alkanes-profile-pfp"
			className="mb-1 flex min-w-0 items-center gap-1.5 text-xs text-muted-foreground transition-colors hover:text-foreground"
		>
			<span className="truncate">
				<span className="font-semibold text-foreground">
					{pfp.name ?? pfp.collection?.name ?? 'Inscription'}
				</span>
				{pfp.collection && pfp.name !== pfp.collection.name ? ` · ${pfp.collection.name}` : ''}
				{pfp.num !== null ? ` · Inscription #${pfp.num.toLocaleString()}` : ''}
			</span>
			<ExternalLink className="size-3 shrink-0" aria-hidden="true" />
		</a>
	)
}

function AddressRow({ address, copied, copy }: Pick<ProfileProps, 'address' | 'copied' | 'copy'>) {
	return (
		<div className="flex items-center gap-1 text-xs text-muted-foreground">
			<span className="truncate font-mono">{truncateMiddle(address, 12, 10)}</span>
			<button
				type="button"
				onClick={copy}
				aria-label={copied ? 'Copied' : `Copy address ${address}`}
				className="grid size-6 shrink-0 place-items-center rounded transition-colors hover:bg-secondary hover:text-foreground"
			>
				{copied ? <Check className="size-3 text-success-ink" /> : <Copy className="size-3" />}
			</button>
			<a
				href={`https://espo.sh/address/${encodeURIComponent(address)}`}
				target="_blank"
				rel="noreferrer"
				aria-label="Open on espo.sh"
				title="Open on espo.sh"
				className="grid size-6 shrink-0 place-items-center rounded transition-colors hover:bg-secondary hover:text-foreground"
			>
				<ExternalLink className="size-3" aria-hidden="true" />
			</a>
		</div>
	)
}

/// The owner's door to the app-wide profile editor.
function EditProfileButton({ hasAccount }: { hasAccount: boolean }) {
	return (
		<button
			type="button"
			data-testid="alkanes-profile-edit"
			onClick={openProfileEditor}
			className="mb-1 rounded-lg bg-secondary px-3 py-1.5 text-xs font-bold transition-colors hover:bg-muted"
		>
			{hasAccount ? 'Edit profile' : 'Set up profile'}
		</button>
	)
}

function ProfileAvatar({
	base,
	profile,
	size
}: {
	base: ProfileProps['base']
	profile?: TwetchProfile
	size: number
}) {
	if (base) {
		// The full profile read is fresher than the batch identity cache — an
		// avatar just picked in the editor shows without waiting on the batch.
		const user = profile?.icon ? { ...base, icon: profile.icon } : base
		return <Avatar key={user.icon ?? ''} user={user} userId={base.id} size={size} />
	}
	return (
		<span
			style={{ width: size, height: size }}
			className="grid place-items-center rounded-full bg-muted"
		>
			<User className="size-7 text-muted-foreground" aria-hidden="true" />
		</span>
	)
}

/// Unrealized PnL over the exactly-priced portion of the position — the
/// fomo-style triangle, but only where an exact frBTC cost basis exists.
/// Minted/transferred-in positions show a quiet dash with the reason.
function PnlPct({ pct, why }: { pct: number | null; why?: string }) {
	if (pct === null || !Number.isFinite(pct)) {
		return (
			<span data-testid="alkanes-profile-pnl" className="block text-xxs text-muted-foreground" title={why}>
				—
			</span>
		)
	}
	const up = pct >= 0
	return (
		<span
			data-testid="alkanes-profile-pnl"
			aria-label={`unrealized ${up ? 'gain' : 'loss'} ${Math.abs(pct).toFixed(2)} percent`}
			className={cn(
				'inline-flex items-baseline gap-0.5 text-xxs tabular-nums',
				up ? 'text-success-ink' : 'text-destructive-ink'
			)}
		>
			<span aria-hidden="true" className="text-[0.7em] leading-none">
				{up ? '▲' : '▼'}
			</span>
			{Math.abs(pct) >= 1000 ? `${(Math.abs(pct) / 1000).toFixed(1)}K` : Math.abs(pct).toFixed(2)}
			%
		</span>
	)
}

function PositionRow({
	p,
	pools,
	navigate,
	ledger
}: {
	p: Position
	pools: ProfileProps['pools']
	navigate: ProfileProps['navigate']
	ledger?: LedgerToken
}) {
	const mid = p.id === BTC_ID ? null : frbtcMidPriceScaled(pools, p.id)
	const tradable = p.id !== BTC_ID && mid !== null
	// PnL over the exact-sats basis only — the ledger's honesty split. A
	// position acquired by mint/transfer has no exact cost; no number is
	// fabricated for it.
	let pnlPct: number | null = null
	let pnlWhy: string | undefined
	if (p.id !== BTC_ID) {
		if (ledger) {
			const satsNow = mid !== null ? (p.raw * mid) / 10_000_000_000_000_000n : null
			const lp = ledgerPosition(ledger, p.raw, satsNow)
			if (lp.unrealizedSats !== null && lp.remainingBasisSats > 0n) {
				pnlPct = (Number(lp.unrealizedSats) / Number(lp.remainingBasisSats)) * 100
			} else {
				pnlWhy =
					lp.disclosure.length > 0
						? `No exact cost basis — ${lp.disclosure.join(', ')}`
						: 'No exact cost basis in this wallet’s history'
			}
		} else {
			pnlWhy = 'Cost basis unavailable'
		}
	}
	const row = (
		<React.Fragment>
			{p.icon ? (
				<img
					src={p.icon}
					alt=""
					width={28}
					height={28}
					className="size-7 shrink-0 rounded-full object-cover"
				/>
			) : (
				<span className="grid size-7 shrink-0 place-items-center rounded-full bg-secondary text-xxs font-bold text-muted-foreground">
					{p.name.slice(0, 2).toUpperCase()}
				</span>
			)}
			<span className="min-w-0 flex-1">
				<span className="block truncate text-xs font-semibold">{p.name}</span>
				<span className="block truncate text-xxs tabular-nums text-muted-foreground">
					{formatAmount(p.raw, amountDp(p.raw))} {p.name}
				</span>
			</span>
			<span className="shrink-0 text-right text-xs tabular-nums">
				<span className="block font-semibold">{p.usd !== null ? usdCompact(p.usd) : '—'}</span>
				{p.id === BTC_ID ? null : <PnlPct pct={pnlPct} why={pnlWhy} />}
			</span>
		</React.Fragment>
	)
	if (tradable) {
		return (
			<button
				type="button"
				onClick={() => navigate('home', p.id)}
				className="flex w-full items-center gap-2.5 rounded-lg px-2 py-1.5 text-left transition-colors hover:bg-secondary/50"
			>
				{row}
			</button>
		)
	}
	return <div className="flex w-full items-center gap-2.5 rounded-lg px-2 py-1.5">{row}</div>
}

function HoldingsCard(props: ProfileProps) {
	const { positions, totalUsd, balancesLoading, balancesUnavailable, pools, navigate } = props
	return (
		<div className="rounded-2xl bg-card p-4">
			<div className="flex items-baseline justify-between">
				<h3 className="text-sm font-semibold">Holdings</h3>
				<span className="text-sm font-bold tabular-nums">
					{totalUsd !== null ? usdCompact(totalUsd) : ''}
				</span>
			</div>
			{balancesUnavailable ? (
				<p
					data-testid="alkanes-profile-balances-unavailable"
					className="py-6 text-center text-xs text-muted-foreground"
				>
					Balances could not load — unavailable, not zero.
				</p>
			) : balancesLoading && positions.length === 0 ? (
				<p className="py-6 text-center text-xs text-muted-foreground">Loading…</p>
			) : positions.length === 0 ? (
				<p className="py-6 text-center text-xs text-muted-foreground">No alkanes in this wallet.</p>
			) : (
				<ul className="mt-2 space-y-0.5" data-testid="alkanes-profile-holdings">
					{positions.map((p) => (
						<li key={p.id}>
							<PositionRow
								p={p}
								pools={pools}
								navigate={navigate}
								ledger={props.ledgerTokens?.get(p.id)}
							/>
						</li>
					))}
				</ul>
			)}
		</div>
	)
}

/// The curve itself: a plain area sparkline, colored by the window's
/// direction. Non-scaling stroke so the aspect-fit stretch never fattens it.
function EquityCurve({ points }: { points: EquityPoint[] }) {
	const usds = points.map((p) => p.usd)
	const min = Math.min(...usds)
	const max = Math.max(...usds)
	const span = max - min || 1
	const step = 100 / Math.max(points.length - 1, 1)
	const coords = points.map(
		(p, i) => `${(i * step).toFixed(2)},${(37 - ((p.usd - min) / span) * 34).toFixed(2)}`
	)
	const up = usds[usds.length - 1] >= usds[0]
	return (
		<svg
			viewBox="0 0 100 40"
			preserveAspectRatio="none"
			role="img"
			aria-label="Holdings value over time"
			data-testid="alkanes-equity-curve"
			className={cn('h-28 w-full', up ? 'text-success-ink' : 'text-destructive-ink')}
		>
			<polygon points={`0,40 ${coords.join(' ')} 100,40`} fill="currentColor" opacity="0.08" />
			<polyline
				points={coords.join(' ')}
				fill="none"
				stroke="currentColor"
				strokeWidth="1.5"
				vectorEffect="non-scaling-stroke"
			/>
		</svg>
	)
}

// ── desktop: the own-profile page shape, worn by any wallet ────────────────

function DesktopProfile(props: ProfileProps) {
	const {
		address,
		base,
		profile,
		totalUsd,
		cashUsd,
		history,
		balancesUnavailable,
		own
	} = props
	const banner = resolveTwetchImage(profile?.banner)
	// Daily-close resolution: 24H would be two points pretending to be a
	// curve, so the shortest honest window is a week.
	const [win, setWin] = React.useState<'7D' | '30D' | '90D' | 'ALL'>('90D')
	const winDays = { '7D': 7, '30D': 30, '90D': 90, ALL: 365 }[win]
	// One request for the whole year; windows are sliced here, so a chip
	// click is instant instead of a fresh server build per window.
	const equityAll = useEquity(address, 365)
	const equity = React.useMemo(() => {
		const from = Date.now() / 1000 - winDays * 86_400
		return { ...equityAll, points: equityAll.points.filter((p) => p.ts >= from) }
	}, [equityAll, winDays])
	const [swapFilter, setSwapFilter] = React.useState<'all' | 'buys' | 'sells'>('all')
	const trades = history.rows.filter((r) => r.kind === 'buy' || r.kind === 'sell').length

	return (
		// Full-bleed with the WORKSPACE'S exact outer paddings (gap-2 px-2
		// pt-2) so switching between the trading screen and a profile causes
		// zero layout shift — the rail sits at the same x both places. The
		// token rail rides along at xl (sticky, its own scroll) so the coins
		// stay one click away; the follow rail waits for 2xl, where all three
		// columns have real width.
		<div className="flex w-full gap-2 px-2 pb-12 pt-2">
			<div className="sticky top-16 hidden h-[calc(100dvh-6.5rem)] w-[340px] shrink-0 self-start xl:block">
				<WorkspaceRail />
			</div>

			<div className="min-w-0 flex-1">
				{(balancesUnavailable || history.unavailable) && (
					<div
						role="status"
						data-testid="alkanes-profile-data-unavailable"
						className="mb-4 rounded-2xl border border-warning/30 bg-warning/10 px-4 py-3 text-xs leading-relaxed"
					>
						{balancesUnavailable && history.unavailable
							? 'Wallet balances and trade history could not refresh.'
							: balancesUnavailable
							? 'Wallet balances could not refresh.'
							: 'On-chain trade history could not refresh.'}{' '}
						Missing data is shown as unavailable, not zero.
					</div>
				)}

				{/* Identity: the Twetch banner and name when the wallet is linked,
				    the address itself when it is not. */}
				<div className="overflow-hidden rounded-2xl bg-card">
					{banner ? (
						<img src={banner} alt="" className="h-24 w-full object-cover" />
					) : (
						<div className="h-24 bg-secondary/40" aria-hidden="true" />
					)}
					<div className="flex flex-wrap items-end gap-4 px-5 pb-1">
						<span className="-mt-8 inline-block rounded-full ring-4 ring-card">
							<ProfileAvatar base={base} profile={profile} size={64} />
						</span>
						<div className="min-w-0 flex-1">
							<IdentityName base={base} profile={profile} address={address} />
							<p className="text-xs text-muted-foreground">
								{trades > 0
									? `${trades} ${trades === 1 ? 'trade' : 'trades'}`
									: history.unavailable
									? 'Trade history unavailable'
									: 'No trades yet'}
								{socialProfiles && <span className="mx-1.5">·</span>}
								{socialProfiles && profile ? (
									<span>
										{profile.numFollowing ?? 0} Following · {profile.numFollowers ?? 0} Followers
									</span>
								) : socialProfiles ? (
									<span title="Follows and followers arrive with a linked Twetch account">
										— Following · — Followers
									</span>
								) : null}
							</p>
						</div>
						{own && (
							<EditProfileButton hasAccount={base !== undefined} />
						)}
						{base && !own && <FollowButton userId={base.id} className="mb-1" />}
						{base && (
							<a
								href={`${TWETCH_WEB}/u/${base.id}`}
								target="_blank"
								rel="noreferrer"
								className="mb-1 inline-flex items-center gap-1 rounded-lg bg-secondary px-3 py-1.5 text-xs font-bold transition-colors hover:bg-muted"
							>
								View on Twetch
								<ExternalLink className="size-3" aria-hidden="true" />
							</a>
						)}
					</div>
					<div className="px-5 pb-4 pt-1.5">
						{profile?.description && (
							<p className="mb-1.5 whitespace-pre-wrap text-xs leading-relaxed text-muted-foreground">
								{profile.description}
							</p>
						)}
						<PfpRow icon={profile?.icon ?? base?.icon} />
						<AddressRow address={address} copied={props.copied} copy={props.copy} />
					</div>
				</div>

				{/* Portfolio value + the equity-curve slot. */}
				<div className="mt-4 grid grid-cols-2 gap-4">
					<div className="rounded-2xl bg-card p-4">
						<div className="flex items-start justify-between">
							<div>
								<p className="block text-3xl font-bold tabular-nums">
									{totalUsd !== null ? (
										`$${totalUsd.toLocaleString('en-US', {
											minimumFractionDigits: 2,
											maximumFractionDigits: 2
										})}`
									) : (
										<span className="text-muted-foreground">—</span>
									)}
								</p>
								<p className="mt-0.5 text-xs text-muted-foreground">
									Portfolio value · change over time unknown until balance snapshots ship
								</p>
							</div>
							<div className="flex items-center gap-1">
								{(['7D', '30D', '90D', 'ALL'] as const).map((w) => (
									<button
										key={w}
										type="button"
										aria-pressed={win === w}
										onClick={() => setWin(w)}
										className={cn(
											'rounded-md px-1.5 py-0.5 text-xxs font-semibold tabular-nums transition-colors',
											win === w
												? 'bg-foreground text-background'
												: 'text-muted-foreground hover:text-foreground'
										)}
									>
										{w}
									</button>
								))}
							</div>
						</div>
						{/* The equity curve: real end-of-day balances × real daily
						    closes, or the honest empty frame while the series is
						    unavailable — never a reconstruction-by-vibes. */}
						{equity.points.length >= 2 ? (
							<div className="mt-3 rounded-xl bg-secondary/30 p-2">
								<EquityCurve points={equity.points} />
								<p className="px-1 pt-1 text-xxs leading-relaxed text-muted-foreground">
									Alkane holdings at daily closes · BTC cash not included
									{equity.truncated ? ' · history incomplete — a floor, not the story' : ''}
									{equity.unpriced.length > 0
										? ` · ${equity.unpriced.length} ${
												equity.unpriced.length === 1 ? 'token' : 'tokens'
										  } without a price series excluded`
										: ''}
								</p>
							</div>
						) : (
							<div
								data-testid="alkanes-equity-placeholder"
								className="mt-3 grid h-32 place-items-center rounded-xl bg-secondary/30"
							>
								<p className="max-w-[32ch] px-3 text-center text-xxs leading-relaxed text-muted-foreground">
									{equity.isLoading
										? 'Building the equity curve…'
										: 'No equity curve yet — it draws from the wallet’s replayed daily balances and our candle series, and neither covers this window.'}
								</p>
							</div>
						)}
						<div className="mt-3 flex items-center justify-between rounded-xl bg-secondary/40 px-3 py-2.5">
							<div>
								<p className="block text-xs text-muted-foreground">Cash (BTC)</p>
								<p className="block text-sm font-bold tabular-nums">
									{cashUsd !== null ? usdCompact(cashUsd) : '—'}
								</p>
							</div>
							{own && (
								<div className="flex gap-2">
									{/* The SAME send flow /wallet's own button opens — the
									    command palette's UTXO/send surface, one click. */}
									<button
										type="button"
										data-testid="alkanes-profile-send"
										onClick={() => event.emit('command', { id: 'manage-utxos' })}
										className="rounded-lg bg-secondary px-3 py-1.5 text-xs font-bold transition-colors hover:bg-muted"
									>
										Send
									</button>
									{/* Receiving IS the address — copy it, no modal. */}
									<button
										type="button"
										data-testid="alkanes-profile-receive"
										onClick={props.copy}
										className="rounded-lg bg-primary px-3 py-1.5 text-xs font-bold text-primary-foreground transition-colors hover:bg-primary/90"
									>
										{props.copied ? 'Copied!' : 'Receive'}
									</button>
								</div>
							)}
						</div>
					</div>

					{/* History — every alkane action of THIS address: the ledger's
					    exact rows, plus what is in the mempool right now (and, on
					    the owner's own screen, what this browser just signed). */}
					<div className="rounded-2xl bg-card p-4">
						<div className="flex items-center gap-1">
							{(
								[
									['all', 'All history'],
									['buys', 'Buys'],
									['sells', 'Sells']
								] as const
							).map(([id, label]) => (
								<button
									key={id}
									type="button"
									aria-pressed={swapFilter === id}
									data-testid={`alkanes-swaps-filter-${id}`}
									onClick={() => setSwapFilter(id)}
									className={cn(
										'rounded-md px-2 py-1 text-xs font-semibold transition-colors',
										swapFilter === id
											? 'bg-foreground text-background'
											: 'text-muted-foreground hover:text-foreground'
									)}
								>
									{label}
								</button>
							))}
						</div>
						<div
							className="-mx-2 mt-2 max-h-[280px] overflow-y-auto"
							data-testid="alkanes-profile-swaps"
						>
							<HistoryList
								address={address}
								own={own}
								compact
								kinds={
									swapFilter === 'buys'
										? ['buy']
										: swapFilter === 'sells'
										? ['sell']
										: undefined
								}
							/>
						</div>
					</div>
				</div>

				{/* The real holdings, full width under the grid. */}
				<div className="mt-4">
					<HoldingsCard {...props} />
				</div>
			</div>

			<FollowRail className="hidden w-[320px] shrink-0 2xl:block" />
		</div>
	)
}

// ── phone: the stacked column ──────────────────────────────────────────────

function PhoneProfile(props: ProfileProps) {
	const { address, base, profile, own } = props
	const banner = resolveTwetchImage(profile?.banner)
	return (
		<React.Fragment>
			{/* ── identity ── */}
			<div className="overflow-hidden rounded-2xl bg-card">
				{banner ? (
					<img src={banner} alt="" className="h-28 w-full object-cover" />
				) : (
					<div className="h-28 bg-secondary/40" aria-hidden="true" />
				)}
				<div className="px-4 pb-4">
					<div className="flex items-end justify-between">
						<span className="-mt-8 inline-block rounded-full ring-4 ring-card">
							<ProfileAvatar base={base} profile={profile} size={64} />
						</span>
						{own && (
							<EditProfileButton hasAccount={base !== undefined} />
						)}
						{base && !own && <FollowButton userId={base.id} className="mb-1" />}
						{base && (
							<a
								href={`${TWETCH_WEB}/u/${base.id}`}
								target="_blank"
								rel="noreferrer"
								className="mb-1 inline-flex items-center gap-1 rounded-lg bg-secondary px-3 py-1.5 text-xs font-bold transition-colors hover:bg-muted"
							>
								View on Twetch
								<ExternalLink className="size-3" aria-hidden="true" />
							</a>
						)}
					</div>

					<div className="mt-2">
						<IdentityName base={base} profile={profile} address={address} />
					</div>

					{profile?.description && (
						<p className="mt-1 whitespace-pre-wrap text-xs leading-relaxed text-muted-foreground">
							{profile.description}
						</p>
					)}

					{socialProfiles && profile && (
						<p className="mt-2 text-xs text-muted-foreground">
							<span className="font-semibold text-foreground">{profile.numFollowers ?? 0}</span>{' '}
							Followers
							<span className="mx-1.5">·</span>
							<span className="font-semibold text-foreground">{profile.numFollowing ?? 0}</span>{' '}
							Following
						</p>
					)}

					<div className="mt-3">
						<PfpRow icon={profile?.icon ?? base?.icon} />
						<AddressRow address={address} copied={props.copied} copy={props.copy} />
					</div>
				</div>
			</div>

			{/* ── holdings ── */}
			<div className="mt-3">
				<HoldingsCard {...props} />
			</div>

			{/* ── history: the ledger's rows + the mempool right now ── */}
			<div className="mt-3 overflow-hidden rounded-2xl bg-card">
				<h3 className="px-4 pt-4 text-sm font-semibold">History</h3>
				<div className="mt-1" data-testid="alkanes-profile-swaps">
					<HistoryList address={address} own={own} compact />
				</div>
			</div>
		</React.Fragment>
	)
}
